/**
 * Color Design Tokens
 * Single source of truth for all color values
 */

export const colors = {
  // Primary Brand Colors (Enterprise Blue)
  primary: {
    50: '#e8f4fd',
    100: '#cce5ff',
    200: '#66b3ff',
    300: '#3399ff',
    400: '#0069ba', // 2026-10-12 (D2): was #0078d4, 4.27:1 for 11px link/action text on a tint
    500: '#0067b8', // Main primary color
    600: '#004080',
    700: '#003366',
    800: '#002855',
    900: '#001d3d',
    main: '#0067b8',
    dark: '#002855',
    light: '#cce5ff',
    tint: '#dbeafb', // light brand wash: active tab pill, rail-adjacent surfaces (--hl)
    wash: '#f4f8fc', // idle-tab hover fill
    contrastText: '#FFFFFF',
  },

  // Primary overlay helpers (translucent backgrounds)
  primaryOverlay: {
    subtle: 'rgba(0, 120, 212, 0.05)',
    light: 'rgba(0, 120, 212, 0.10)',
    medium: 'rgba(0, 120, 212, 0.15)',
    strong: 'rgba(0, 120, 212, 0.25)',
  },

  // Success Colors
  // .main = standard success; .dark = achievement-within-success (deeper green)
  // for "approved > validated" overlay. Style guide §2.4.
  // 2026-10-12 (DEV walk D2): #107c10 -> #0e6e0e, 'Approved' chips measured 3.96-4.03:1 at 11px.
  success: {
    50: '#dff6dd',
    100: '#dff6dd',
    200: '#dff6dd',
    300: '#dff6dd',
    400: '#0e6e0e',
    500: '#0e6e0e',
    600: '#0e6e0e',
    700: '#0e6e0e',
    800: '#0e6e0e',
    900: '#0b5a0b',
    main: '#0e6e0e',
    dark: '#0b5a0b',
    light: '#dff6dd',
    contrastText: '#FFFFFF',
  },

  // Warning Colors
  // .main = attention-needed orange; .light = medium-severity yellow-orange hue
  // (NOT a pale BG — that's .50). Consumer: Data Gap "Medium" tier. Style guide §2.4.
  warning: {
    50: '#fff4ce',
    100: '#fff4ce',
    200: '#fff4ce',
    300: '#ffb74d',
    400: '#ff8c00',
    500: '#ff8c00',
    600: '#ff8c00',
    700: '#ff8c00',
    800: '#ff8c00',
    900: '#ff8c00',
    main: '#ff8c00',
    // 2026-10-12 contrast sweep: .dark was #ff8c00 (2.3:1 as alert text on white and on its own tint) and
    // contrastText was white on the orange fill (2.3:1). Dark amber ink for text; dark ink on the fill.
    dark: '#8f5200',
    light: '#ffb74d',
    contrastText: '#1a1a1a',
  },

  // Error Colors
  // .main = standard error red; .dark = highest-severity crimson for Type 1 findings
  // and the dark end of error gradients. Style guide §2.4.
  // 2026-10-12 (DEV walk D2): #d13438 -> #bd2327, 11px error chips measured 4.1:1 on their tint.
  error: {
    50: '#fde7e9',
    100: '#fde7e9',
    200: '#fde7e9',
    300: '#fde7e9',
    400: '#bd2327',
    500: '#bd2327',
    600: '#bd2327',
    700: '#bd2327',
    800: '#bd2327',
    900: '#9b1b1e',
    main: '#bd2327',
    dark: '#9b1b1e',
    light: '#fde7e9',
    contrastText: '#FFFFFF',
  },

  // Neutral/Grayscale Colors (Warm grays)
  neutral: {
    50: '#f7f7f7',
    100: '#ededed',
    200: '#d4d4d4',
    300: '#a3a3a3',
    400: '#858585',
    500: '#6b6b6b',
    600: '#525252',
    700: '#404040',
    800: '#2d2d2d',
    900: '#1a1a1a',
  },

  // Text Colors
  text: {
    primary: '#1a1a1a',
    secondary: '#404040',
    // 2026-10-12 contrast sweep: tertiary #6b6b6b -> #666666 (4.36:1 on the primary tint, 4.5 needed);
    // placeholder #858585 -> #707070 (3.4-3.7:1 on every field surface, 4.5 needed).
    tertiary: '#666666',
    disabled: '#858585',
    inverse: '#FFFFFF',
    placeholder: '#707070', // §2.3 --text-placeholder (raised from --gray-400, see above)
    link: '#0067b8', // §2.3 --text-link → --primary
  },

  // Background Colors
  background: {
    // Note: the gradient background is applied via MuiCssBaseline; this remains a solid fallback.
    default: '#f7f7f7',
    paper: '#FFFFFF',
    secondary: '#fafbfd',
    tertiary: '#f0f4f9',
  },

  // Semantic Surface Aliases (style guide §2.3)
  surface: {
    page: '#f7f7f7', // --gray-50
    primary: '#ffffff', // --white
    secondary: '#f7f7f7', // --gray-50
    elevated: '#ffffff', // --white
    hover: '#ededed', // --gray-100
    active: '#e8f4fd', // --primary-50
  },

  // Border Colors
  border: {
    light: '#ededed',
    medium: '#d4d4d4',
    dark: '#a3a3a3',
    subtle: '#ededed', // §2.3 --border-subtle → --gray-100
    strong: '#a3a3a3', // §2.3 --border-strong → --gray-300
    interactive: '#0078d4', // §2.3 --border-interactive → --primary-400
    // The edge of an input/select/outlined control. WCAG 1.4.11 wants 3:1 against the surface it sits on;
    // `medium` (#d4d4d4) is 1.5:1 and stays the decorative divider.
    control: '#858585',
  },

  // Disabled controls. Opacity-based dimming composited a pale-blue label onto a pale-blue fill (1.9:1);
  // a disabled control is now a flat neutral fill with a solid ink that still reads (>= 3:1, here ~5:1).
  disabled: { fill: '#e1e1e1', ink: '#5c5c5c' },

  // Backdrop / Glassmorphism
  backdrop: {
    glass: 'rgba(255, 255, 255, 0.85)',
    glassBorder: 'rgba(255, 255, 255, 0.3)',
  },

  // Accent Colors
  accent: {
    // Gold (Premium / CTA)
    gold: '#ffc627',
    goldDark: '#d4a300',
    goldLight: '#fff0b3',
    goldGlow: 'rgba(255, 198, 39, 0.30)',
    // Teal (Decorative / Section Accent — dark surfaces only)
    teal: '#0ea5e9',
    tealDark: '#0284c7',
    tealLight: '#e0f7ff',
    // Surface polish literals
    surfaceHi: '#fafbfd',
    surfaceTint: '#f5f7fb',
    backgroundTint: '#f0f4f9',
  },
} as const;

/**
 * Dark Mode Color Tokens
 * Per style guide §2.6 — complete dark mode mapping
 */
export const colorsDark = {
  // Primary Brand Colors — brighter for dark bg contrast
  primary: {
    50: '#001d3d',
    100: '#002855',
    200: '#003366',
    300: '#004080',
    // #0078d4 measured 2.6–3.0:1 as text and icon ink on the card surface (400 is the ramp's text/icon
    // step), and #3399ff measured 3.95:1 on a primary-tinted chip. #66b3ff clears 4.5:1 on both.
    400: '#66b3ff',
    500: '#3399ff',
    600: '#66b3ff',
    700: '#cce5ff',
    800: '#e8f4fd',
    900: '#f0f8ff',
    main: '#3399ff',
    dark: '#0067b8',
    light: '#66b3ff',
    tint: '#002855', // dark-mode counterpart of the light tint (primary[100] on the inverted ramp)
    wash: '#001d3d', // dark-mode counterpart of the idle-tab hover fill (primary[50] on the inverted ramp)
    // Dark ink: every surface this sits on (main #3399ff, [600] #66b3ff, and the contained-button hover
    // [600]→[700] #cce5ff) is light, where white measured 2.9:1 and under 1.6:1 on hover.
    contrastText: '#1a1a1a',
  },

  // Primary overlay helpers — recalculated for #3399ff base
  primaryOverlay: {
    subtle: 'rgba(51, 153, 255, 0.08)',
    light: 'rgba(51, 153, 255, 0.12)',
    medium: 'rgba(51, 153, 255, 0.18)',
    strong: 'rgba(51, 153, 255, 0.30)',
  },

  // Success — brighter green for dark-mode legibility
  success: {
    50: 'rgba(76, 175, 80, 0.08)',
    100: 'rgba(76, 175, 80, 0.12)',
    200: 'rgba(76, 175, 80, 0.15)',
    300: 'rgba(76, 175, 80, 0.20)',
    400: '#4caf50',
    500: '#4caf50',
    600: '#66bb6a',
    700: '#81c784',
    800: '#a5d6a7',
    900: '#c8e6c9',
    main: '#74c778', // 2026-10-12 (D2): was #4caf50, 3.6:1 as 11px chip ink on the success tint
    dark: '#388e3c',
    light: 'rgba(76, 175, 80, 0.15)',
    contrastText: '#1a1a1a', // white measured 2.8:1 on #4caf50
  },

  // Warning — softer orange on dark
  // .light = medium-severity hue (Data Gap "Medium"); .300-level stays as translucent
  // BG overlay pattern for chip backgrounds.
  warning: {
    50: 'rgba(255, 183, 77, 0.08)',
    100: 'rgba(255, 183, 77, 0.12)',
    200: 'rgba(255, 183, 77, 0.15)',
    300: 'rgba(255, 183, 77, 0.20)',
    400: '#ffb74d',
    500: '#ffb74d',
    600: '#ffc107',
    700: '#ffd54f',
    800: '#ffe082',
    900: '#fff8e1',
    main: '#ffb74d',
    dark: '#ff8c00',
    light: '#ffd54f',
    contrastText: '#1a1a1a',
  },

  // Error — brighter red on dark
  // .dark = deep crimson for Type 1 findings on dark surfaces (lower luminance than .main
  // by design — severity gradient darkens toward critical).
  error: {
    50: 'rgba(239, 83, 80, 0.08)',
    100: 'rgba(239, 83, 80, 0.12)',
    200: 'rgba(239, 83, 80, 0.15)',
    300: 'rgba(239, 83, 80, 0.20)',
    400: '#ef5350',
    500: '#ef5350',
    600: '#f44336',
    700: '#e57373',
    800: '#ef9a9a',
    900: '#ffcdd2',
    // #ef5350 measured 3.95:1 as text on the card surface; #f87171 clears 4.5:1 and carries dark ink.
    main: '#ff9a95', // 2026-10-12 (D2): was #f87171, 3.6:1 for 11px chips on a .2-.25 error tint
    dark: '#b71c1c',
    light: 'rgba(239, 83, 80, 0.15)',
    contrastText: '#1a1a1a',
  },

  // Neutral — dark mode surfaces
  neutral: {
    50: '#1a1a1a',
    100: '#2d2d2d',
    200: '#404040',
    300: '#525252',
    400: '#6b6b6b',
    500: '#858585',
    600: '#a3a3a3',
    700: '#d4d4d4',
    800: '#ededed',
    900: '#f7f7f7',
  },

  // Text — inverted for dark mode
  text: {
    primary: '#ffffff',
    // 2026-10-12 contrast sweep: secondary #a3a3a3 -> #bdbdbd. It was 4.11:1 on the #404040 secondary
    // surface; raising it also keeps the secondary/tertiary tiers apart now that tertiary moves up too.
    secondary: '#bdbdbd',
    // ⚖️ RULING R-Q14 (2026-08-28) — RAISED #858585 -> #949494. The style guide contradicted
    // ITSELF: §2.7 requires "4.5:1 for text" and Principle 4 calls AA "the floor ... not a
    // remediation pass", yet this token on `background.paper` (#2d2d2d) measured 3.73:1 — so every
    // dark-mode caption, timestamp and meta line on a CARD failed the guide's own rule.
    // #949494 is the SMALLEST value that clears it: 4.54:1 on paper (#2d2d2d), 5.74:1 on the page
    // (#1a1a1a). Verified with the WCAG relative-luminance formula, not eyeballed.
    // ⛔ Do NOT "simplify" this to the dark `secondary` (#a3a3a3): that passes at 5.46:1 but
    // collapses the visual distance between the secondary and tertiary tiers, trading a contrast
    // defect for a hierarchy one. The operator was given that option and did not take it.
    // 2026-10-12: R-Q14's #949494 measured 4.17:1 on the #333 field surface and 3.77:1 on a hovered row;
    // #a3a3a3 clears both, and `secondary` above moved up with it so the two tiers stay distinct.
    tertiary: '#a3a3a3',
    disabled: '#8a8a8a', // was #6b6b6b (2.6:1 on the card, 2.4:1 on #404040); 3:1 floor for disabled on every dark surface
    inverse: '#1a1a1a',
    placeholder: '#a3a3a3', // was #525252 (1.8:1). Placeholders carry meaning: 4.5:1 on every field surface
    link: '#66b3ff', // was #3399ff, 3.5:1 on the #404040 surface and 3.9:1 on a hovered row
  },

  // Background — dark surfaces
  background: {
    default: '#1a1a1a',
    paper: '#2d2d2d',
    // 2026-10-12 contrast sweep: #404040 -> #383838. The tertiary text tier was 4.11:1 on #404040 and
    // ~15 call sites draw ink on this surface; one step darker clears 4.5:1 for every text tier.
    secondary: '#383838',
    tertiary: '#383838',
    gradientStart: '#111318', // §2.5 Page gradient (dark) start
    gradientEnd: '#0a0d14', // §2.5 Page gradient (dark) end
  },

  // Semantic Surface Aliases (style guide §2.3)
  surface: {
    page: '#1a1a1a', // --gray-900
    primary: '#2d2d2d', // --gray-800
    // 2026-10-12: #404040 -> #383838 with background.secondary (text tiers were 4.11:1 on #404040).
    secondary: '#383838',
    elevated: '#383838',
    hover: 'rgba(255, 255, 255, 0.06)',
    active: 'rgba(0, 120, 212, 0.15)',
  },

  // Border — dark mode
  border: {
    light: '#404040',
    medium: '#525252',
    dark: '#6b6b6b',
    subtle: '#2d2d2d', // §2.3 --border-subtle → --gray-800
    strong: '#6b6b6b', // §2.3 --border-strong → --gray-500
    interactive: '#3399ff', // §2.3 --border-interactive → --primary-300
    control: '#8a8a8a', // input/select edge, 3:1 on the card and field surfaces (was `medium` #525252, 1.6:1)
  },

  // Disabled controls — dark. Translucent white over the card, composited by the browser.
  disabled: { fill: 'rgba(255, 255, 255, 0.12)', ink: 'rgba(255, 255, 255, 0.62)' },

  // Backdrop / Glassmorphism — dark mode
  backdrop: {
    glass: 'rgba(45, 45, 45, 0.85)',
    glassBorder: 'rgba(255, 255, 255, 0.1)',
  },

  // Accent — gold and teal stay the same per spec
  accent: {
    gold: '#ffc627',
    goldDark: '#d4a300',
    goldLight: '#fff0b3',
    goldGlow: 'rgba(255, 198, 39, 0.30)',
    teal: '#0ea5e9',
    tealDark: '#0284c7',
    tealLight: '#e0f7ff',
    surfaceHi: '#333333',
    surfaceTint: '#2a2a2a',
    backgroundTint: '#222222',
  },
} as const;

export type ColorToken = typeof colors;
